import { computed, getCurrentInstance, onScopeDispose } from 'vue';
import { useNow } from '@vueuse/core';
import type { IOrder } from '@/interfaces/IOrder';
import { AlertType } from '@/interfaces/IAlert';
import { getTableReservations } from '@/domain/reservations/pos-table-hints';
import {
  formatTime,
  getReservationTimeZone,
} from '@/domain/reservations/reservation-time';
import { lateMinutes } from '@/domain/reservations/reservation-status';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useUi } from '@/composition/use-ui';
import { useReservationActions } from './use-reservation-actions';
import { useReservationsUpcoming } from './use-reservations-upcoming';

export function usePosReservationBanner(
  props: {
    readonly tableId: number;
    readonly tableOrders: IOrder[];
    readonly canCreateOrder: boolean;
  },
  onSeated: (tableId: number) => void
) {
  const restaurant = useRestaurantStore();
  const upcoming = useReservationsUpcoming();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const now = useNow({ interval: 30_000 });
  const rows = computed(() =>
    getTableReservations(upcoming.list.value ?? [], props.tableId)
  );
  const reservation = computed(() => rows.value[0] ?? null);
  const actions = useReservationActions(reservation);
  const timeZone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const seated = computed(() => reservation.value?.status === 'seated');
  const timeLabel = computed(() =>
    reservation.value
      ? formatTime(
          seated.value && reservation.value.seatedAt
            ? reservation.value.seatedAt
            : reservation.value.startsAt,
          timeZone.value
        )
      : ''
  );
  const late = computed(() =>
    reservation.value
      ? lateMinutes(
          reservation.value,
          now.value,
          upcoming.settings.value?.toleranceMin ?? 15
        )
      : 0
  );
  const canSeat = computed(
    () => actions.canManage.value && props.canCreateOrder && !seated.value
  );
  const hasDepositToApply = computed(
    () =>
      seated.value &&
      reservation.value?.depositStatus === 'paid' &&
      !reservation.value.depositAppliedAt
  );
  const needsCaja = computed(
    () =>
      !!restaurant.selectedBranch?.settingUseCajas &&
      (!restaurant.caja || restaurant.caja.status !== 'open')
  );
  const applyDisabled = computed(
    () =>
      !actions.canOrders.value ||
      !props.tableOrders.length ||
      needsCaja.value ||
      actions.busy.value
  );
  let alive = true;
  onScopeDispose(() => {
    alive = false;
  });

  async function seat() {
    if (!canSeat.value || !reservation.value) return;
    const id = reservation.value.id;
    const tableId = props.tableId;
    const branchId = restaurant.selectedBranch?.id;
    const restaurantId = restaurant.restaurant?.id;
    const name =
      reservation.value.guestName ||
      String(proxy.$t('reservations.common.anonymous'));
    const result = await actions.seat(undefined, {
      confirmOccupied: props.tableOrders.length > 0,
      announce: false,
    });
    if (
      !result ||
      !alive ||
      id !== reservation.value?.id ||
      tableId !== props.tableId ||
      branchId !== restaurant.selectedBranch?.id ||
      restaurantId !== restaurant.restaurant?.id
    )
      return;
    ui.showToast(
      AlertType.Success,
      String(proxy.$t('reservations.pos.seated', { name }))
    );
    if (props.canCreateOrder) onSeated(result.tableId);
  }

  function applyDeposit() {
    if (!hasDepositToApply.value || applyDisabled.value) return;
    return actions.applyDeposit({
      orderIds: props.tableOrders.map((order) => order.id),
      cajaId: restaurant.caja?.id ?? null,
    });
  }

  function view() {
    if (
      !actions.canManage.value ||
      !reservation.value ||
      !restaurant.restaurant
    )
      return;
    void proxy.$router.push({
      path: `/${restaurant.restaurant.stringId}/reservations`,
      query: { tab: 'today', r: String(reservation.value.id) },
    });
  }
  return {
    reservation,
    rows,
    actions,
    seated,
    timeLabel,
    late,
    canSeat,
    hasDepositToApply,
    needsCaja,
    applyDisabled,
    seat,
    applyDeposit,
    view,
  };
}
